import type { z } from 'zod';
import {
  Appointment,
  Blackout,
  Certificate,
  ClientDetail,
  ClientDurationWritten,
  CreditAdded,
  Deleted,
  HoursWritten,
  Preference,
  Product,
  Reservation,
  Resource,
  SeatSold,
  Service,
  Settlement,
  Stay,
} from '../wire';
import type { IntentKind, OutboxEntry } from './entry';

/**
 * Every kind of write the PWA can make, as data: the request it becomes and the
 * schema its answer is parsed with.
 *
 * A table rather than a `switch` in the drain loop, for the reason `core-api`'s
 * route registry gives for the same move: adding a write means adding a row, and
 * a row with no request shape does not compile. The drain has no idea what an
 * appointment is — it sends intents and settles them — which is why it can be
 * tested exhaustively against §8.2 without a single view being involved.
 *
 * Note what is *not* here: nothing computes a price, a duration or an end time.
 * `CreateAppointmentBody` has no field for any of them and the server looks them
 * up from `service_id` (obligation B7). An offline device holding stale prices
 * that it then asserts on reconnect is exactly the failure that rule exists to
 * prevent, and the shape of the request makes it unrepresentable here.
 */

export interface IntentRequest {
  method: 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  path: string;
  body?: Record<string, unknown> | undefined;
  schema: z.ZodType<unknown>;
}

export function describeIntent(entry: OutboxEntry): IntentRequest {
  const t = entry.target;
  const body = { ...entry.payload, client_intent_id: entry.id };

  switch (entry.kind) {
    case 'appointment.create':
      return { method: 'POST', path: '/appointments', body, schema: Appointment };
    case 'appointment.reschedule':
      return {
        method: 'POST',
        path: `/appointments/${required(t, 'appointment_id')}/reschedule`,
        body,
        schema: Appointment,
      };
    /*
     * S1 — its own path because it is its own verb. The body names a length
     * (and, when the calendar snapped to one of her lengths, the option it is)
     * and never a price: the start does not move, the house gap is re-derived
     * server-side through `occupiedSpan`, and the price is either untouched or
     * the named option's, read from the catalog by the server (B7).
     */
    case 'appointment.duration':
      return {
        method: 'POST',
        path: `/appointments/${required(t, 'appointment_id')}/duration`,
        body,
        schema: Appointment,
      };
    case 'appointment.cancel':
      return {
        method: 'POST',
        path: `/appointments/${required(t, 'appointment_id')}/cancel`,
        body,
        schema: Appointment,
      };
    case 'appointment.status':
      return {
        method: 'POST',
        path: `/appointments/${required(t, 'appointment_id')}/status`,
        body,
        schema: Appointment,
      };
    case 'client.create':
      return { method: 'POST', path: '/clients', body, schema: ClientDetail };
    case 'client.update':
      return {
        method: 'PATCH',
        path: `/clients/${required(t, 'client_id')}`,
        body,
        schema: ClientDetail,
      };
    case 'client.archive':
      return {
        method: 'POST',
        path: `/clients/${required(t, 'client_id')}/archive`,
        body,
        schema: ClientDetail,
      };
    case 'client.identity.add':
      return {
        method: 'POST',
        path: `/clients/${required(t, 'client_id')}/identities`,
        body,
        schema: ClientDetail,
      };
    case 'client.identity.remove':
      return {
        method: 'POST',
        path: `/clients/${required(t, 'client_id')}/identities/remove`,
        body,
        schema: ClientDetail,
      };
    case 'preference.create':
      return {
        method: 'POST',
        path: `/clients/${required(t, 'client_id')}/preferences`,
        body,
        schema: Preference,
      };
    case 'preference.update':
      return {
        method: 'PATCH',
        path: `/clients/${required(t, 'client_id')}/preferences/${required(t, 'preference_id')}`,
        body,
        schema: Preference,
      };
    case 'preference.delete':
      // A `DELETE` carries no body, so `client_intent_id` has nowhere to ride.
      // The `Idempotency-Key` header is the only form of intent here — which is
      // precisely why the outbox always sends the header as well as the field.
      return {
        method: 'DELETE',
        path: `/clients/${required(t, 'client_id')}/preferences/${required(t, 'preference_id')}`,
        schema: Deleted,
      };
    /*
     * A1 — her lengths. A `PUT` because the key *is* the fact: "how long does
     * this take her" has one answer, so a correction replaces rather than adds.
     * That also makes the intent naturally idempotent on top of the memo: two
     * replays of "she is a 35" leave one row saying 35.
     *
     * With an `option_id` on the target the door is the option's
     * (`…/options/{option_id}`, 0048): the same two verbs, on one length of
     * the service rather than on the service however it is sold. The option is
     * in the path and never in the body, because the `DELETE` beside it has no
     * body and one shape for both verbs is one rule.
     */
    case 'duration.set':
      return {
        method: 'PUT',
        path: durationPath(t),
        body,
        schema: ClientDurationWritten,
      };
    case 'duration.clear':
      // A `DELETE` carries no body, so `client_intent_id` has nowhere to ride —
      // the `Idempotency-Key` header is the only form of intent, exactly as for
      // `preference.delete` above.
      return {
        method: 'DELETE',
        path: durationPath(t),
        schema: ClientDurationWritten,
      };
    case 'service.create':
      return { method: 'POST', path: '/services', body, schema: Service };
    case 'service.update':
      return {
        method: 'PATCH',
        path: `/services/${required(t, 'service_id')}`,
        body,
        schema: Service,
      };
    /* S2 — the team. The same two routes Settings' resource writes would use;
       there is exactly one door onto `resources` and this is it. */
    case 'resource.create':
      return { method: 'POST', path: '/resources', body, schema: Resource };
    case 'resource.update':
      return {
        method: 'PATCH',
        path: `/resources/${required(t, 'resource_id')}`,
        body,
        schema: Resource,
      };
    case 'hours.set':
      return { method: 'PUT', path: '/settings/hours', body, schema: HoursWritten };
    case 'blackout.create':
      return { method: 'POST', path: '/blackouts', body, schema: Blackout };
    case 'blackout.delete':
      return {
        method: 'DELETE',
        path: `/blackouts/${required(t, 'blackout_id')}`,
        schema: Deleted,
      };

    /*
     * MVP 4. Note what these bodies do *not* carry, which is the same thing
     * `appointment.create` does not carry and for a stronger reason: **no
     * total.** A checkout names its lines and the instruments the guest has;
     * the server runs the waterfall (spec §6.5) and decides every amount. The
     * till previews the arithmetic so the owner can watch it move
     * (`money/waterfall.ts`), and none of that preview is in this request —
     * there is nowhere in `CreateSettlementBody` to put it. An offline device
     * asserting what a sale came to is B7's mistake wearing a different hat.
     */
    case 'settlement.create':
      return { method: 'POST', path: '/settlements', body, schema: Settlement };
    case 'certificate.issue':
      return { method: 'POST', path: '/certificates', body, schema: Certificate };
    case 'credit.add':
      return {
        method: 'POST',
        path: `/clients/${required(t, 'client_id')}/credit`,
        body,
        schema: CreditAdded,
      };
    case 'product.create':
      return { method: 'POST', path: '/products', body, schema: Product };
    case 'product.update':
      return {
        method: 'PATCH',
        path: `/products/${required(t, 'product_id')}`,
        body,
        schema: Product,
      };

    /*
     * C1 → B4. The move the till makes, and the only reservation transition that
     * is queued rather than sent: every other one is a button on the
     * Reservations screen, which is online by `useRemote`'s argument.
     *
     * Idempotent on replay by the same mechanism everything else here is —
     * `client_intent_id` rides the body and `Idempotency-Key` rides the header,
     * both derived from the entry's own ULID, and `core-api`'s `runIntent`
     * returns the memoized answer rather than walking the transition table a
     * second time. That matters more here than elsewhere: `collected → collected`
     * is not a legal move, so a replay *without* the memo would come back 409 and
     * park a completed sale in the owner's task list.
     */
    case 'reservation.status':
      return {
        method: 'POST',
        path: `/reservations/${required(t, 'reservation_id')}/status`,
        body,
        schema: Reservation,
      };

    /*
     * V5 — the desk sells what the site sells. Note, again, what the bodies do
     * not carry: no rate, no fare, no total. `CreateStayBody` has room for a
     * room type and two dates; `CreateDepartureBookingBody` for tier ids and
     * seat counts. The number the sheet showed before the button came from
     * `GET /stays/quote` or the manifest's tiers, and it stays on the screen.
     */
    case 'stay.book':
      return { method: 'POST', path: '/stays', body, schema: Stay };
    case 'departure.book':
      return {
        method: 'POST',
        path: `/departures/instances/${required(t, 'instance_id')}/bookings`,
        body,
        schema: SeatSold,
      };
  }
}

/**
 * What the settled response means for the mirror.
 *
 * Kept beside the request shapes rather than in the mirror, because the pairing
 * "this intent produced that kind of row" is one fact and belongs in one file.
 */
export type SettlementKind =
  | 'appointment'
  | 'client'
  | 'preference'
  | 'duration'
  | 'service'
  | 'resource'
  | 'hours'
  | 'blackout'
  | 'settlement'
  | 'certificate'
  | 'credit'
  | 'product'
  | 'none';

export function settlementKind(kind: IntentKind): SettlementKind {
  if (kind.startsWith('appointment.')) return 'appointment';
  // Before `client.`, which would otherwise swallow it: the noun in
  // `credit.add`'s *target* is a client, but what came back is a ledger entry.
  if (kind === 'credit.add') return 'credit';
  if (kind.startsWith('client.')) return 'client';
  if (kind.startsWith('preference.')) return 'preference';
  if (kind.startsWith('duration.')) return 'duration';
  if (kind.startsWith('service.')) return 'service';
  // Before `reservation.` and after `service.` — no overlap either way, but the
  // ordering of this ladder is load-bearing enough elsewhere to keep it explicit.
  if (kind.startsWith('resource.')) return 'resource';
  if (kind === 'hours.set') return 'hours';
  if (kind.startsWith('blackout.')) return 'blackout';
  if (kind === 'settlement.create') return 'settlement';
  if (kind === 'certificate.issue') return 'certificate';
  if (kind.startsWith('product.')) return 'product';
  // `reservation.status` lands here on purpose: the shelf is **not mirrored**
  // (`Reservations.tsx` is a `useRemote` screen, for the reason the kitchen pass
  // is), so there is no IndexedDB row for a settled answer to update. The screen
  // re-reads it from the server the next time somebody opens it. `stay.book`
  // and `departure.book` land here for the same reason: the front desk and
  // the dock are online screens, and the sheet that queued the sale awaits the
  // drain and re-reads.
  return 'none';
}

/**
 * `/clients/{id}/durations/{service_id}`, or that with `/options/{option_id}`
 * when the target names a length. The option is optional on the *target* and
 * required on the *path* once present, so a half-written target cannot become
 * a request for the broad row by accident.
 */
function durationPath(target: Record<string, string>): string {
  const broad = `/clients/${required(target, 'client_id')}/durations/${required(target, 'service_id')}`;
  return target['option_id'] === undefined
    ? broad
    : `${broad}/options/${required(target, 'option_id')}`;
}

function required(target: Record<string, string>, key: string): string {
  const value = target[key];
  if (value === undefined) {
    throw new Error(`intent is missing target.${key}`);
  }
  return value;
}
